class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => {
      if (event.target.id === 'CartDrawer-Overlay') this.close();
    });
    this.setHeaderCartIconAccessibility();
    this.recommendationsObserver = new MutationObserver(() => {
      const slot = this.querySelector('[data-panel-recommendations]');
      if (this.classList.contains('active') && slot && slot !== this.recommendationsSlot) this.loadRecommendations();
    });
    this.recommendationsObserver.observe(this, { childList: true, subtree: true });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.removeAttribute('data-added');
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.removeAttribute('data-added');
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const wasOpen = this.classList.contains('active');
    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    requestAnimationFrame(() => {
      if (!this.classList.contains('active')) return;
      const container = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : this.querySelector('#CartDrawer');
      const focusElement = this.querySelector('.drawer__close') || this.querySelector('.drawer__inner');
      trapFocus(container, focusElement);
    });
    if (!wasOpen) this.querySelector('cart-drawer-items')?.dispatchViewEvent();
    this.loadRecommendations();
  }

  close() {
    this.classList.remove('active');
    this.removeAttribute('data-added');
    this.recommendationsController?.abort();
    const restoreTarget = this.activeElement?.isConnected ? this.activeElement : document.querySelector('#cart-icon-bubble');
    removeTrapFocus(restoreTarget);
    document.body.classList.remove('overflow-hidden');
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.setAttribute('data-added', '');
    this.addedProductId = parsedState.product_id || parsedState.items?.[0]?.product_id;
    this.addedVariantId = String(parsedState.id || parsedState.items?.[0]?.id || '');
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const html = parsedState.sections?.[section.id];
      if (!sectionElement || !html) return;
      const sectionContent = this.getSectionDOM(html, section.selector || '.shopify-section');
      if (sectionContent) sectionElement.innerHTML = sectionContent.innerHTML;
    });
    const latestInput = Array.from(this.querySelectorAll('[data-quantity-variant-id]'))
      .find((input) => input.dataset.quantityVariantId === this.addedVariantId);
    const latestRow = latestInput?.closest('.cart-item');
    if (latestRow) latestRow.parentElement.prepend(latestRow);
    this.open();
  }

  async loadRecommendations() {
    const slot = this.querySelector('[data-panel-recommendations]');
    const shell = this.querySelector('#CartDrawer');
    const productId = this.addedProductId || shell?.dataset.productId;
    if (!slot || !productId || !shell?.dataset.recommendationsUrl) return;
    this.recommendationsController?.abort();
    this.recommendationsSlot = slot;
    const controller = new AbortController();
    this.recommendationsController = controller;
    const previousFocus = document.activeElement;
    try {
      for (const intent of ['complementary', 'related']) {
        const url = new URL(shell.dataset.recommendationsUrl, window.location.origin);
        url.search = new URLSearchParams({
          section_id: 'skr-cart-panel-recommendations',
          product_id: productId,
          limit: '10',
          intent,
        }).toString();
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) continue;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = html.querySelector('[data-panel-recommendations-result]');
        if (!content || content.hidden) continue;
        if (intent === 'complementary' && content.dataset.source === 'fallback') continue;
        if (controller.signal.aborted || !slot.isConnected) return;
        if (slot.contains(document.activeElement) && document.activeElement !== previousFocus) return;
        slot.replaceChildren(content);
        if (this.classList.contains('active')) {
          trapFocus(this.querySelector('#CartDrawer'), document.activeElement);
        }
        return;
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('Cart recommendations unavailable');
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return this.getSectionDOM(html, selector)?.innerHTML || '';
  }

  getSectionsToRender() {
    return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    if (!this.contains(element)) this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  onCartUpdate(event) {
    if (event?.source === 'product-form') return Promise.resolve();
    return super.onCartUpdate();
  }

  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('.skr-product__form, .skr-home__add-form')) return;
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;
  event.preventDefault();
  if (form.dataset.skrAdding === 'true') return;
  const button = event.submitter || form.querySelector('[name="add"]');
  if (!button || button.disabled) return;
  const wasDisabled = button.disabled;
  form.dataset.skrAdding = 'true';
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');
  let errorMessage = form.querySelector('[data-skr-cart-error]');
  if (errorMessage) errorMessage.hidden = true;
  try {
    const body = new FormData(form);
    body.set('sections', drawer.getSectionsToRender().map((section) => section.id).join(','));
    body.set('sections_url', window.location.pathname);
    drawer.setActiveElement(button);
    const response = await fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      body,
    });
    const cartData = await response.json();
    if (!response.ok || cartData.status) throw new Error(cartData.description || cartData.message || window.cartStrings.error);
    drawer.renderContents(cartData);
    publish(PUB_SUB_EVENTS.cartUpdate, {
      source: 'product-form',
      productVariantId: body.get('id'),
      cartData,
    });
  } catch (error) {
    if (!errorMessage) {
      errorMessage = document.createElement('p');
      errorMessage.dataset.skrCartError = '';
      errorMessage.setAttribute('role', 'alert');
      form.append(errorMessage);
    }
    errorMessage.textContent = error.message || window.cartStrings.error;
    errorMessage.hidden = false;
  } finally {
    delete form.dataset.skrAdding;
    button.disabled = wasDisabled || form.querySelector('[data-variant] option:checked')?.dataset.available === 'false';
    button.removeAttribute('aria-busy');
  }
}, true);
